import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider } from '../contexts/AuthContext';
import LoginPage from './auth/LoginPage';
import ProtectedRoute from './auth/ProtectedRoute';
import MainLayout from './layout/MainLayout';
import Dashboard from './Dashboard';
import AdminDashboard from './admin/AdminDashboard';
import { PenholderDashboard } from './PenholderDashboard';
import { SubmissionVersionHistory } from './SubmissionVersionHistory';
import { ErrorBoundary } from './shared';

// AWS Console-inspired theme based on official AWS color palette
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#252F3E', // AWS Navy - main navigation and headers
      light: '#48A3C6', // AWS Blue accent
      dark: '#232F3E', // Darker navy for emphasis
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FF9900', // AWS Orange - call-to-actions and highlights
      light: '#FFB84D',
      dark: '#CC7700',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F5F5F5', // Light gray background
      paper: '#FFFFFF', // White cards and panels
    },
    text: {
      primary: '#111111', // Nearly black for main text
      secondary: '#666666', // Gray for secondary text
      disabled: '#CCCCCC', // Light gray for disabled text
    },
    divider: '#CCCCCC', // Borders and dividers
    action: {
      hover: 'rgba(37, 47, 62, 0.04)', // Subtle hover effect
      selected: 'rgba(37, 47, 62, 0.08)',
      disabled: '#CCCCCC',
    },
    info: {
      main: '#007EB9', // AWS Blue for informational elements
      light: '#48A3C6',
      dark: '#005A85',
    },
    success: {
      main: '#2E7D32', // Green for success states
      light: '#4CAF50',
      dark: '#1B5E20',
    },
    warning: {
      main: '#FF9900', // AWS Orange for warnings
      light: '#FFB84D',
      dark: '#CC7700',
    },
    error: {
      main: '#D32F2F', // Red for errors
      light: '#F44336',
      dark: '#B71C1C',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      color: '#252F3E',
      fontWeight: 600,
    },
    h2: {
      color: '#252F3E',
      fontWeight: 600,
    },
    h3: {
      color: '#252F3E',
      fontWeight: 600,
    },
    h4: {
      color: '#252F3E',
      fontWeight: 600,
    },
    h5: {
      color: '#252F3E',
      fontWeight: 600,
    },
    h6: {
      color: '#252F3E',
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F5F5F5',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#252F3E',
          color: '#FFFFFF',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#232F3E',
          color: '#FFFFFF',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', // AWS style - no uppercase transformation
          borderRadius: 4,
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: '#252F3E',
          '&:hover': {
            backgroundColor: '#1A252F',
          },
        },
        containedSecondary: {
          backgroundColor: '#FF9900',
          '&:hover': {
            backgroundColor: '#E68A00',
          },
        },
        outlinedPrimary: {
          borderColor: '#252F3E',
          color: '#252F3E',
          '&:hover': {
            backgroundColor: 'rgba(37, 47, 62, 0.04)',
            borderColor: '#252F3E',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #CCCCCC',
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        colorPrimary: {
          backgroundColor: '#252F3E',
          color: '#FFFFFF',
        },
        colorSecondary: {
          backgroundColor: '#FF9900',
          color: '#FFFFFF',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F5F5F5',
          '& .MuiTableCell-head': {
            color: '#252F3E',
            fontWeight: 600,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          width: '100%',
          minWidth: 'unset',
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: 'none !important',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 153, 0, 0.08)', // AWS Orange selection
            '&:hover': {
              backgroundColor: 'rgba(255, 153, 0, 0.12)',
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#CCCCCC',
            },
            '&:hover fieldset': {
              borderColor: '#252F3E',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FF9900',
            },
          },
        },
      },
    },
  },
});

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <AuthProvider>
          <Router>
            <LoginPage>
              <Routes>
                <Route 
                  path="/" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Dashboard />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute adminOnly>
                      <MainLayout>
                        <AdminDashboard />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/penholder" 
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <PenholderDashboard />
                      </MainLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </LoginPage>
          </Router>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;